import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, Video, MapPin, DollarSign, AlertTriangle, CheckCircle, XCircle, Users, TrendingUp, Banknote, CreditCard, ExternalLink, Plus, Edit, Trash2, Download, BarChart3, Filter } from "lucide-react";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Pricing configuration
const PRICING = {
  "F2F-60": 60,
  "F2F-90": 90,
  "Online-60": 50,
  "Online-90": 75,
};

// Status colors
const STATUS_COLORS = {
  "Scheduled": "bg-blue-500",
  "Completed": "bg-green-500",
  "Late-Cancel": "bg-orange-500",
  "No-Charge": "bg-gray-500",
};

const PAYMENT_STATUS_COLORS = {
  "Paid": "bg-green-500 text-white",
  "Pending": "bg-red-500 text-white",
};

// v12.8.4: Lesson History Tab — filter + inline Edit (Pencil) modal
function LessonHistoryTab({ sessions, getStudentById, calculatePrice, onUpdated }) {
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Edit modal state
  const [editing, setEditing] = useState(null); // session being edited
  const [editForm, setEditForm] = useState({ session_date: "", status: "Scheduled", payment_status: "Pending", price: "0" });
  const [saving, setSaving] = useState(false);

  const filteredSessions = useMemo(() => {
    let filtered = [...sessions].sort((a, b) => b.session_date.localeCompare(a.session_date));
    if (categoryFilter !== "all") {
      filtered = filtered.filter(s => {
        const sids = s.student_ids || (s.student_id ? [s.student_id] : []);
        return sids.some(id => {
          const st = getStudentById(id);
          if (!st) return false;
          const classes = [...(st.class_types || []), st.class_type || ''];
          if (categoryFilter === "private") return classes.some(c => c === "Private Lessons" || c === "Private Only");
          if (categoryFilter === "remote") return classes.some(c => c === "Remote/International" || c === "International");
          return false;
        });
      });
    }
    if (dateFrom) filtered = filtered.filter(s => s.session_date >= dateFrom);
    if (dateTo) filtered = filtered.filter(s => s.session_date <= dateTo);
    return filtered;
  }, [sessions, categoryFilter, dateFrom, dateTo, getStudentById]);

  const openEdit = (session) => {
    const price = session.price != null ? session.price : calculatePrice(session.type, session.start_time, session.end_time);
    setEditing(session);
    setEditForm({
      session_date: session.session_date || "",
      status: session.status || "Scheduled",
      payment_status: session.payment_status || "Pending",
      price: String(price ?? "0"),
    });
  };

  const closeEdit = () => { if (!saving) setEditing(null); };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.session_date) { toast.error("Session date is required"); return; }
    setSaving(true);
    try {
      const payload = {
        session_date: editForm.session_date,
        status: editForm.status,
        payment_status: editForm.payment_status,
        price: parseFloat(editForm.price) || 0,
      };
      await axios.put(`${API}/private-sessions/${editing.id}`, payload);
      toast.success("Session updated");
      setEditing(null);
      if (onUpdated) await onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to update session");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-[#18181b] border-white/10 rounded-sm">
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-[#A1A1AA] text-xs block mb-1">Category</label>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              data-testid="lesson-history-category-filter"
              className="h-9 px-3 bg-[#27272a] border border-white/10 text-white rounded-sm text-sm">
              <option value="all">All Sessions</option>
              <option value="private">Private Lessons</option>
              <option value="remote">Remote/International</option>
            </select>
          </div>
          <div>
            <label className="text-[#A1A1AA] text-xs block mb-1">From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="h-9 px-3 bg-[#27272a] border border-white/10 text-white rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-[#A1A1AA] text-xs block mb-1">To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="h-9 px-3 bg-[#27272a] border border-white/10 text-white rounded-sm text-sm" />
          </div>
          <p className="text-[#A1A1AA] text-sm self-end">{filteredSessions.length} session(s)</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-2 text-[#A1A1AA]">Date</th>
                <th className="text-left p-2 text-[#A1A1AA]">Student</th>
                <th className="text-left p-2 text-[#A1A1AA]">Type</th>
                <th className="text-left p-2 text-[#A1A1AA]">Time</th>
                <th className="text-left p-2 text-[#A1A1AA]">Status</th>
                <th className="text-left p-2 text-[#A1A1AA]">Payment</th>
                <th className="text-right p-2 text-[#A1A1AA]">Amount</th>
                <th className="text-center p-2 text-[#A1A1AA]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0 ? (
                <tr><td colSpan={8} className="text-center p-6 text-[#A1A1AA]">No sessions found for selected filters</td></tr>
              ) : filteredSessions.map(s => {
                const sids = s.student_ids || (s.student_id ? [s.student_id] : []);
                const names = sids.map(id => getStudentById(id)?.name || 'Unknown').join(', ');
                const price = s.price != null ? s.price : calculatePrice(s.type, s.start_time, s.end_time);
                return (
                  <tr key={s.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-2 text-white">{s.session_date}</td>
                    <td className="p-2 text-white font-medium">{names}</td>
                    <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${s.type === 'F2F' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>{s.type}</span></td>
                    <td className="p-2 text-[#A1A1AA]">{s.start_time} - {s.end_time}</td>
                    <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs text-white ${STATUS_COLORS[s.status] || 'bg-gray-500'}`}>{s.status}</span></td>
                    <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${s.payment_status === 'Paid' ? 'text-green-400' : 'text-red-400'}`}>{s.payment_status}</span></td>
                    <td className="p-2 text-right text-[#D4AF37] font-medium">£{price}</td>
                    <td className="p-2 text-center">
                      <button
                        type="button"
                        onClick={() => openEdit(s)}
                        data-testid={`lesson-history-edit-${s.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-sm hover:bg-[#D4AF37]/20 text-[#D4AF37] transition"
                        title="Edit session"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Edit Modal */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Edit Session
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Student</Label>
                <p className="text-white font-medium">
                  {(editing.student_ids || (editing.student_id ? [editing.student_id] : []))
                    .map(id => getStudentById(id)?.name || 'Unknown').join(', ')}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Session Date</Label>
                <Input
                  type="date"
                  value={editForm.session_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, session_date: e.target.value }))}
                  data-testid="edit-session-date"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Status</Label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                    data-testid="edit-session-status"
                    className="w-full h-10 px-3 bg-[#27272a] border border-white/10 text-white rounded-sm text-sm"
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="Completed">Completed</option>
                    <option value="Late-Cancel">Cancelled</option>
                    <option value="No-Charge">No-Charge</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Payment Status</Label>
                  <select
                    value={editForm.payment_status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, payment_status: e.target.value }))}
                    data-testid="edit-session-payment-status"
                    className="w-full h-10 px-3 bg-[#27272a] border border-white/10 text-white rounded-sm text-sm"
                  >
                    <option value="Paid">Paid</option>
                    <option value="Pending">Unpaid</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Amount (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.price}
                  onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                  data-testid="edit-session-amount"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeEdit}
              disabled={saving}
              data-testid="edit-session-cancel"
              className="bg-transparent border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={saving}
              data-testid="edit-session-save"
              className="bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function PrivateLessons() {
  const [activeTab, setActiveTab] = useState("calendar");
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    student_ids: [],
    session_date: "",
    start_time: "10:00",
    end_time: "11:00",
    type: "F2F",
    status: "Scheduled",
    payment_status: "Pending",
    payment_method: "",
    sifu_notes: "",
    price: "60",
  });
  
  // Student search (multi-select)
  const [studentSearch, setStudentSearch] = useState("");
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  
  // Video link modal
  const [showVideoLinkModal, setShowVideoLinkModal] = useState(false);
  const [selectedStudentForVideo, setSelectedStudentForVideo] = useState(null);
  const [videoLink, setVideoLink] = useState("");
  
  // v3.4: Financial period filter
  const [financialPeriod, setFinancialPeriod] = useState("month");
  const [searchTerm, setSearchTerm] = useState("");
  
  // v3.7: Fee payments data for 4-stream financial system
  const [feePayments, setFeePayments] = useState([]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, sessionsRes, feePaymentsRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/private-sessions`),
        axios.get(`${API}/fee-payments`).catch(() => ({ data: [] })),
      ]);
      setStudents(studentsRes.data || []);
      setSessions(sessionsRes.data || []);
      setFeePayments(feePaymentsRes.data || []);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter students for search (exclude already selected)
  const filteredStudents = useMemo(() => {
    if (!studentSearch) return [];
    return students.filter(s => 
      s.name.toLowerCase().includes(studentSearch.toLowerCase()) &&
      !bookingForm.student_ids.includes(s.id)
    ).slice(0, 8);
  }, [students, studentSearch, bookingForm.student_ids]);

  // Get student by ID
  const getStudentById = useCallback((id) => {
    return students.find(s => s.id === id);
  }, [students]);

  // Calculate session price
  const calculatePrice = (type, startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const start = new Date(`2000-01-01 ${startTime}`);
    const end = new Date(`2000-01-01 ${endTime}`);
    const durationMins = (end - start) / (1000 * 60);
    const duration = durationMins >= 75 ? 90 : 60;
    const key = `${type}-${duration}`;
    return PRICING[key] || 60;
  };

  // Today's sessions
  const todaySessions = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return sessions
      .filter(s => s.session_date === today)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [sessions]);

  // Unpaid past sessions (alerts)
  const unpaidPastSessions = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return sessions.filter(s => 
      s.session_date < today && 
      s.payment_status === "Pending" &&
      s.status !== "No-Charge"
    );
  }, [sessions]);

  // Calendar events
  const calendarEvents = useMemo(() => {
    return sessions.map(session => {
      const sids = session.student_ids || (session.student_id ? [session.student_id] : []);
      const names = sids.map(id => getStudentById(id)?.name || 'Unknown').join(', ');
      const price = session.price != null ? session.price : calculatePrice(session.type, session.start_time, session.end_time);
      return {
        id: session.id,
        title: `${names} (${session.type})`,
        start: `${session.session_date}T${session.start_time}`,
        end: `${session.session_date}T${session.end_time}`,
        backgroundColor: session.payment_status === "Paid" ? "#22c55e" : "#ef4444",
        borderColor: STATUS_COLORS[session.status]?.replace("bg-", "#") || "#3b82f6",
        extendedProps: { ...session, studentName: names, price },
      };
    });
  }, [sessions, getStudentById]);

  // Handle date click on calendar
  const handleDateClick = (info) => {
    setEditingSession(null);
    setBookingForm({
      student_ids: [],
      session_date: info.dateStr.split('T')[0],
      start_time: "10:00",
      end_time: "11:00",
      type: "F2F",
      status: "Scheduled",
      payment_status: "Pending",
      payment_method: "",
      sifu_notes: "",
      price: String(calculatePrice("F2F", "10:00", "11:00")),
    });
    setStudentSearch("");
    setShowBookingModal(true);
  };

  // Handle event click on calendar
  const handleEventClick = (info) => {
    const session = info.event.extendedProps;
    const sids = session.student_ids || (session.student_id ? [session.student_id] : []);
    setEditingSession(session);
    setBookingForm({
      student_ids: sids,
      session_date: session.session_date,
      start_time: session.start_time,
      end_time: session.end_time,
      type: session.type,
      status: session.status,
      payment_status: session.payment_status,
      payment_method: session.payment_method || "",
      sifu_notes: session.sifu_notes || "",
      price: session.price != null ? String(session.price) : String(calculatePrice(session.type, session.start_time, session.end_time)),
    });
    setStudentSearch("");
    setShowBookingModal(true);
  };

  // Select student from dropdown (multi-select: add to list)
  const selectStudent = (student) => {
    setBookingForm(prev => ({ ...prev, student_ids: [...prev.student_ids, student.id] }));
    setStudentSearch("");
    setShowStudentDropdown(false);
  };

  // Remove student from selection
  const removeStudent = (studentId) => {
    setBookingForm(prev => ({ ...prev, student_ids: prev.student_ids.filter(id => id !== studentId) }));
  };

  // Save session
  const handleSaveSession = async () => {
    if (bookingForm.student_ids.length === 0 || !bookingForm.session_date) {
      toast.error("Please select at least one student and a date");
      return;
    }

    try {
      const payload = { ...bookingForm, student_id: bookingForm.student_ids[0] };
      if (editingSession) {
        await axios.put(`${API}/private-sessions/${editingSession.id}`, payload);
        toast.success("Session updated");
      } else {
        await axios.post(`${API}/private-sessions`, payload);
        toast.success("Session booked — attendance synced for all students");
      }
      setShowBookingModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save session");
    }
  };

  // Delete session
  const handleDeleteSession = async () => {
    if (!editingSession) return;
    if (!window.confirm("Delete this session?")) return;

    try {
      await axios.delete(`${API}/private-sessions/${editingSession.id}`);
      toast.success("Session deleted");
      setShowBookingModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete session");
    }
  };

  // Quick status update
  const updateSessionStatus = async (sessionId, field, value) => {
    try {
      await axios.put(`${API}/private-sessions/${sessionId}`, { [field]: value });
      fetchData();
      toast.success("Updated");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Launch video lesson
  const launchVideoLesson = (session) => {
    const student = getStudentById(session.student_id);
    if (student?.video_lesson_link) {
      window.open(student.video_lesson_link, '_blank');
    } else {
      setSelectedStudentForVideo(student);
      setVideoLink(student?.video_lesson_link || "");
      setShowVideoLinkModal(true);
    }
  };

  // Save video link
  const saveVideoLink = async () => {
    if (!selectedStudentForVideo) return;
    try {
      await axios.put(`${API}/students/${selectedStudentForVideo.id}`, {
        video_lesson_link: videoLink
      });
      toast.success("Video link saved");
      setShowVideoLinkModal(false);
      fetchData();
      if (videoLink) window.open(videoLink, '_blank');
    } catch (error) {
      toast.error("Failed to save video link");
    }
  };

  // v3.7: Enhanced Financial summary with 4-stream breakdown
  const financialSummary = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Calculate date range based on period
    let startDate, endDate, periodLabel;
    switch (financialPeriod) {
      case "quarter":
        const quarterStart = Math.floor(currentMonth / 3) * 3;
        startDate = new Date(currentYear, quarterStart, 1);
        endDate = new Date(currentYear, quarterStart + 3, 0);
        periodLabel = `Q${Math.floor(currentMonth / 3) + 1} ${currentYear}`;
        break;
      case "6months":
        startDate = new Date(currentYear, currentMonth - 5, 1);
        endDate = new Date(currentYear, currentMonth + 1, 0);
        periodLabel = "Last 6 Months";
        break;
      case "year":
        startDate = new Date(currentYear, 0, 1);
        endDate = new Date(currentYear, 11, 31);
        periodLabel = `Year ${currentYear}`;
        break;
      default: // month
        startDate = new Date(currentYear, currentMonth, 1);
        endDate = new Date(currentYear, currentMonth + 1, 0);
        periodLabel = now.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }
    
    const periodSessions = sessions.filter(s => {
      const d = new Date(s.session_date);
      return d >= startDate && d <= endDate;
    });
    
    // v3.7: Filter fee payments by period
    const periodFeePayments = feePayments.filter(p => {
      const d = new Date(p.date);
      return d >= startDate && d <= endDate;
    });

    let totalRevenue = 0;
    let f2fRevenue = 0;
    let onlineRevenue = 0;
    let paidRevenue = 0;
    let pendingRevenue = 0;
    let cashRevenue = 0;
    let onlinePaymentRevenue = 0;
    let completedCount = 0;
    let cancelledCount = 0;
    
    // v3.7: 4-stream breakdown
    let groupClassRevenue = 0;
    let privateRevenue = 0;
    let gradingRevenue = 0;
    let membershipRevenue = 0;

    // Calculate private session revenue
    periodSessions.forEach(s => {
      const price = s.price != null ? parseFloat(s.price) : calculatePrice(s.type, s.start_time, s.end_time);
      
      if (s.status !== "No-Charge") {
        privateRevenue += price;
        
        if (s.type === "F2F") f2fRevenue += price;
        else onlineRevenue += price;
        
        if (s.payment_status === "Paid") {
          paidRevenue += price;
          if (s.payment_method === "Cash") cashRevenue += price;
          else onlinePaymentRevenue += price;
        } else {
          pendingRevenue += price;
        }
      }
      
      if (s.status === "Completed" || s.status === "Late-Cancel") completedCount++;
      if (s.status === "Late-Cancel") cancelledCount++;
    });
    
    // v3.7: Calculate fee payments revenue by category
    periodFeePayments.forEach(p => {
      const amount = parseFloat(p.amount) || 0;
      switch (p.category) {
        case "group_class":
          groupClassRevenue += amount;
          break;
        case "private":
          // Already counted above
          break;
        case "grading":
          gradingRevenue += amount;
          break;
        case "membership":
          membershipRevenue += amount;
          break;
      }
    });
    
    // Total revenue = all 4 streams
    totalRevenue = privateRevenue + groupClassRevenue + gradingRevenue + membershipRevenue;

    return { 
      totalRevenue, f2fRevenue, onlineRevenue, paidRevenue, pendingRevenue,
      cashRevenue, onlinePaymentRevenue, completedCount, cancelledCount,
      sessionCount: periodSessions.length, periodLabel, startDate, endDate,
      periodSessions, periodFeePayments,
      // v3.7: 4-stream breakdown
      groupClassRevenue, privateRevenue, gradingRevenue, membershipRevenue
    };
  }, [sessions, feePayments, financialPeriod]);

  // v3.7: Enhanced CSV Export with Category
  const exportToCSV = () => {
    // Private sessions data
    const sessionHeaders = ["Date", "Student", "Category", "Time", "Duration", "Type", "Status", "Payment Status", "Payment Method", "Amount", "Notes"];
    const sessionRows = financialSummary.periodSessions.map(s => {
      const student = getStudentById(s.student_id);
      const price = s.price != null ? parseFloat(s.price) : calculatePrice(s.type, s.start_time, s.end_time);
      return [
        new Date(s.session_date).toISOString().split('T')[0],
        student?.name || "Unknown",
        "Private Lesson",
        `${s.start_time}-${s.end_time}`,
        `${Math.round((new Date(`2000-01-01 ${s.end_time}`) - new Date(`2000-01-01 ${s.start_time}`)) / 60000)}min`,
        s.type,
        s.status,
        s.payment_status,
        s.payment_method || "",
        `£${price}`,
        (s.sifu_notes || "").replace(/,/g, ";")
      ];
    });
    
    // Fee payments data
    const feeRows = financialSummary.periodFeePayments.map(p => {
      const student = getStudentById(p.student_id);
      const categoryLabel = p.category === "group_class" ? "Monthly Group Class" :
                           p.category === "grading" ? "Grading Fee" :
                           p.category === "membership" ? "Membership/Insurance" : "Other";
      return [
        new Date(p.date).toISOString().split('T')[0],
        student?.name || "Unknown",
        categoryLabel,
        "",
        "",
        "",
        "",
        "Paid",
        p.payment_method || "",
        `£${p.amount}`,
        (p.notes || "").replace(/,/g, ";")
      ];
    });
    
    const allRows = [...sessionRows, ...feeRows].sort((a, b) => {
      return b[0].localeCompare(a[0]);
    });
    
    const csv = [sessionHeaders.join(","), ...allRows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `loong-fu-financials-${financialPeriod}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported successfully (includes all categories)");
  };

  // Filtered sessions for search
  const filteredSessions = useMemo(() => {
    if (!searchTerm) return financialSummary.periodSessions;
    return financialSummary.periodSessions.filter(s => {
      const student = getStudentById(s.student_id);
      return student?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [financialSummary.periodSessions, searchTerm, getStudentById]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#A1A1AA]">Loading private lessons...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="private-lessons-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Private Lessons 私人課程
        </h1>
        <p className="text-[#A1A1AA] mt-1">Manage 1-on-1 sessions, billing, and scheduling</p>
      </div>

      {/* Sifu Alerts */}
      {(unpaidPastSessions.length > 0 || todaySessions.length > 0) && (
        <Card className="bg-[#18181b] border-[#D4AF37]/50 rounded-sm">
          <CardHeader className="border-b border-white/5 p-4">
            <CardTitle className="text-base font-semibold text-[#D4AF37] uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <AlertTriangle className="w-4 h-4" />
              Sifu Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {/* Unpaid sessions */}
            {unpaidPastSessions.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                <DollarSign className="w-5 h-5 text-red-400" />
                <div>
                  <p className="text-white font-medium">{unpaidPastSessions.length} Unpaid Session(s)</p>
                  <p className="text-[#A1A1AA] text-sm">
                    {unpaidPastSessions.slice(0, 3).map(s => getStudentById(s.student_id)?.name).join(", ")}
                    {unpaidPastSessions.length > 3 && ` +${unpaidPastSessions.length - 3} more`}
                  </p>
                </div>
              </div>
            )}
            {/* Today's sessions */}
            {todaySessions.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                <Clock className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-white font-medium">{todaySessions.length} Session(s) Today</p>
                  <p className="text-[#A1A1AA] text-sm">
                    {todaySessions.map(s => `${s.start_time} - ${getStudentById(s.student_id)?.name}`).join(" | ")}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-[#18181b] border border-white/10 p-1">
          <TabsTrigger value="calendar" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA]">
            <Calendar className="w-4 h-4 mr-2" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="agenda" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-[#A1A1AA]">
            <Clock className="w-4 h-4 mr-2" />
            Daily Agenda
          </TabsTrigger>
          <TabsTrigger value="billing" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-[#A1A1AA]">
            <Banknote className="w-4 h-4 mr-2" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-[#A1A1AA]">
            <Filter className="w-4 h-4 mr-2" />
            Lesson History
          </TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardContent className="p-4">
              <div className="fc-dark-theme" style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: '700px' }}>
                <style>{`
                  .fc-dark-theme .fc {
                    --fc-border-color: rgba(255,255,255,0.1);
                    --fc-page-bg-color: #18181b;
                    --fc-neutral-bg-color: #27272a;
                    --fc-list-event-hover-bg-color: rgba(255,255,255,0.1);
                    --fc-today-bg-color: rgba(212,175,55,0.1);
                  }
                  .fc-dark-theme .fc-toolbar-title { color: white; font-family: 'Oswald', sans-serif; }
                  .fc-dark-theme .fc-col-header-cell-cushion { color: #A1A1AA; }
                  .fc-dark-theme .fc-daygrid-day-number { color: #A1A1AA; }
                  .fc-dark-theme .fc-timegrid-slot-label { color: #A1A1AA; }
                  .fc-dark-theme .fc-button { background: #27272a !important; border-color: rgba(255,255,255,0.1) !important; }
                  .fc-dark-theme .fc-button:hover { background: #3f3f46 !important; }
                  .fc-dark-theme .fc-button-active { background: #D4AF37 !important; color: black !important; }
                  .fc-dark-theme .fc-event { cursor: pointer; }
                `}</style>
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView="timeGridWeek"
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                  }}
                  events={calendarEvents}
                  dateClick={handleDateClick}
                  eventClick={handleEventClick}
                  slotMinTime="08:00:00"
                  slotMaxTime="21:00:00"
                  allDaySlot={false}
                  height="auto"
                  eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Agenda Tab */}
        <TabsContent value="agenda" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Today's Sessions ({new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })})
            </h2>
            <Button onClick={() => { setEditingSession(null); setBookingForm({ ...bookingForm, student_ids: [], session_date: new Date().toISOString().split('T')[0] }); setStudentSearch(""); setShowBookingModal(true); }} className="bg-[#D4AF37] text-black hover:bg-[#B8972E]">
              <Plus className="w-4 h-4 mr-2" />
              Book Session
            </Button>
          </div>

          {todaySessions.length === 0 ? (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">No sessions scheduled for today</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {todaySessions.map(session => {
                const sids = session.student_ids || (session.student_id ? [session.student_id] : []);
                const names = sids.map(id => getStudentById(id)?.name || 'Unknown').join(', ');
                const price = session.price != null ? session.price : calculatePrice(session.type, session.start_time, session.end_time);
                return (
                  <Card key={session.id} className="bg-[#18181b] border-white/10 rounded-sm hover:border-[#D4AF37]/30 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-white">{session.start_time}</p>
                            <p className="text-xs text-[#A1A1AA]">to {session.end_time}</p>
                          </div>
                          <div className="h-12 w-px bg-white/10" />
                          <div>
                            <p className="text-white font-semibold text-lg">{names}{sids.length > 1 && <span className="text-[#D4AF37] text-sm ml-2">({sids.length} students)</span>}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {session.type === "Online" ? (
                                <span className="flex items-center gap-1 text-blue-400 text-sm">
                                  <Video className="w-3 h-3" /> Online
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-green-400 text-sm">
                                  <MapPin className="w-3 h-3" /> Face-to-Face
                                </span>
                              )}
                              <span className={`px-2 py-0.5 text-xs rounded-sm ${STATUS_COLORS[session.status]} text-white`}>
                                {session.status}
                              </span>
                              <span className={`px-2 py-0.5 text-xs rounded-sm ${PAYMENT_STATUS_COLORS[session.payment_status]}`}>
                                £{price} - {session.payment_status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {session.type === "Online" && session.status === "Scheduled" && (
                            <Button size="sm" onClick={() => launchVideoLesson(session)} className="bg-blue-600 hover:bg-blue-700">
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Launch
                            </Button>
                          )}
                          {session.status === "Scheduled" && (
                            <Button size="sm" onClick={() => updateSessionStatus(session.id, "status", "Completed")} className="bg-green-600 hover:bg-green-700">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Complete
                            </Button>
                          )}
                          {session.payment_status === "Pending" && (
                            <Button size="sm" onClick={() => updateSessionStatus(session.id, "payment_status", "Paid")} variant="outline" className="border-green-500 text-green-500 hover:bg-green-500/10">
                              Mark Paid
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleEventClick({ event: { extendedProps: session } })} className="border-white/20 text-white hover:bg-white/10">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Billing Tab - v3.4 Enhanced Financial Reporting */}
        <TabsContent value="billing" className="space-y-4">
          {/* Period Filter */}
          <Card className="bg-[#18181b] border-[#D4AF37]/30 rounded-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-white font-semibold uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Financial Stats: {financialSummary.periodLabel}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-[#A1A1AA]" />
                  <Select value={financialPeriod} onValueChange={setFinancialPeriod}>
                    <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      <SelectItem value="month" className="text-white">This Month</SelectItem>
                      <SelectItem value="quarter" className="text-white">This Quarter</SelectItem>
                      <SelectItem value="6months" className="text-white">6 Months</SelectItem>
                      <SelectItem value="year" className="text-white">This Year</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={exportToCSV} variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-[#18181b] border-[#D4AF37]/30 rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#D4AF37]/20 rounded-sm">
                    <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Total Revenue</p>
                    <p className="text-2xl font-bold text-[#D4AF37]">£{financialSummary.totalRevenue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-green-500/30 rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-sm">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Paid</p>
                    <p className="text-2xl font-bold text-green-400">£{financialSummary.paidRevenue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-red-500/30 rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/20 rounded-sm">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Pending</p>
                    <p className="text-2xl font-bold text-red-400">£{financialSummary.pendingRevenue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/10 rounded-sm">
                    <Users className="w-5 h-5 text-[#A1A1AA]" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Sessions</p>
                    <p className="text-2xl font-bold text-white">{financialSummary.sessionCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-400" />
                  <div>
                    <p className="text-[#A1A1AA] text-xs">F2F Revenue</p>
                    <p className="text-lg font-bold text-white">£{financialSummary.f2fRevenue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-blue-400" />
                  <div>
                    <p className="text-[#A1A1AA] text-xs">Online Revenue</p>
                    <p className="text-lg font-bold text-white">£{financialSummary.onlineRevenue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-green-400" />
                  <div>
                    <p className="text-[#A1A1AA] text-xs">Cash Payments</p>
                    <p className="text-lg font-bold text-white">£{financialSummary.cashRevenue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-400" />
                  <div>
                    <p className="text-[#A1A1AA] text-xs">Online Payments</p>
                    <p className="text-lg font-bold text-white">£{financialSummary.onlinePaymentRevenue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* v3.7: 4-Stream Revenue Breakdown */}
          <Card className="bg-[#18181b] border-[#D4AF37]/30 rounded-sm">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Revenue by Category (4-Stream)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-sm border-l-4 border-blue-500">
                  <div>
                    <p className="text-[#A1A1AA] text-xs">Monthly Fees</p>
                    <p className="text-xl font-bold text-blue-400">£{financialSummary.groupClassRevenue}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-sm border-l-4 border-green-500">
                  <div>
                    <p className="text-[#A1A1AA] text-xs">Private Lessons</p>
                    <p className="text-xl font-bold text-green-400">£{financialSummary.privateRevenue}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-sm border-l-4 border-purple-500">
                  <div>
                    <p className="text-[#A1A1AA] text-xs">Grading Fees</p>
                    <p className="text-xl font-bold text-purple-400">£{financialSummary.gradingRevenue}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-sm border-l-4 border-orange-500">
                  <div>
                    <p className="text-[#A1A1AA] text-xs">Membership/Insurance</p>
                    <p className="text-xl font-bold text-orange-400">£{financialSummary.membershipRevenue}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment History Log */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Payment History ({filteredSessions.length} sessions)
                </CardTitle>
                <Input 
                  placeholder="Search student..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48 bg-white/5 border-white/10 text-white h-8"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-96">
                <table className="w-full">
                  <thead className="sticky top-0 bg-[#18181b]">
                    <tr className="border-b border-white/5">
                      <th className="text-left p-3 text-[#A1A1AA] text-xs uppercase">Date</th>
                      <th className="text-left p-3 text-[#A1A1AA] text-xs uppercase">Student</th>
                      <th className="text-left p-3 text-[#A1A1AA] text-xs uppercase">Time</th>
                      <th className="text-left p-3 text-[#A1A1AA] text-xs uppercase">Type</th>
                      <th className="text-left p-3 text-[#A1A1AA] text-xs uppercase">Status</th>
                      <th className="text-left p-3 text-[#A1A1AA] text-xs uppercase">Payment</th>
                      <th className="text-left p-3 text-[#A1A1AA] text-xs uppercase">Amount</th>
                      <th className="text-left p-3 text-[#A1A1AA] text-xs uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions
                      .sort((a, b) => b.session_date.localeCompare(a.session_date))
                      .map(session => {
                        const sids = session.student_ids || (session.student_id ? [session.student_id] : []);
                        const names = sids.map(id => getStudentById(id)?.name || 'Unknown').join(', ');
                        const price = session.price != null ? session.price : calculatePrice(session.type, session.start_time, session.end_time);
                        return (
                          <tr key={session.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 text-white">{new Date(session.session_date).toLocaleDateString('en-GB')}</td>
                            <td className="p-3 text-white font-medium">{names}{sids.length > 1 ? ` (${sids.length})` : ''}</td>
                            <td className="p-3 text-[#A1A1AA]">{session.start_time} - {session.end_time}</td>
                            <td className="p-3">
                              {session.type === "Online" ? (
                                <span className="text-blue-400"><Video className="w-4 h-4 inline mr-1" />Online</span>
                              ) : (
                                <span className="text-green-400"><MapPin className="w-4 h-4 inline mr-1" />F2F</span>
                              )}
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-1 text-xs rounded-sm ${STATUS_COLORS[session.status]} text-white`}>
                                {session.status}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-1 text-xs rounded-sm ${PAYMENT_STATUS_COLORS[session.payment_status]}`}>
                                {session.payment_status}
                              </span>
                            </td>
                            <td className="p-3 text-white font-medium">£{price}</td>
                            <td className="p-3">
                              <div className="flex gap-1">
                                {session.payment_status === "Pending" && session.status !== "No-Charge" && (
                                  <Button size="sm" variant="ghost" onClick={() => updateSessionStatus(session.id, "payment_status", "Paid")} className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-7 px-2">
                                    <CheckCircle className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => handleEventClick({ event: { extendedProps: session } })} className="text-[#A1A1AA] hover:text-white hover:bg-white/10 h-7 px-2">
                                  <Edit className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lesson History Tab */}
        <TabsContent value="history" className="space-y-4">
          <LessonHistoryTab sessions={sessions} getStudentById={getStudentById} calculatePrice={calculatePrice} onUpdated={fetchData} />
        </TabsContent>
      </Tabs>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {editingSession ? "Edit Session" : "Book Private Session"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Student Multi-Select */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs uppercase">Students</Label>
              {/* Selected student chips */}
              {bookingForm.student_ids.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {bookingForm.student_ids.map(sid => {
                    const s = getStudentById(sid);
                    return (
                      <span key={sid} className="inline-flex items-center gap-1 px-2 py-1 bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] text-xs rounded-sm font-medium">
                        {s?.name || sid}
                        <button onClick={() => removeStudent(sid)} className="ml-1 text-[#D4AF37] hover:text-red-400 font-bold">&times;</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search & add students..."
                  data-testid="student-search-input"
                  value={studentSearch}
                  onChange={(e) => { setStudentSearch(e.target.value); setShowStudentDropdown(true); }}
                  onFocus={() => setShowStudentDropdown(true)}
                  className="bg-white/5 border-white/10 text-white"
                />
                {showStudentDropdown && filteredStudents.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-[#27272a] border border-white/10 rounded-sm max-h-48 overflow-y-auto">
                    {filteredStudents.map(s => (
                      <button key={s.id} onClick={() => selectStudent(s)} className="w-full text-left px-3 py-2 hover:bg-white/10 text-white text-sm">
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Date</Label>
                <Input type="date" value={bookingForm.session_date} onChange={(e) => setBookingForm(prev => ({ ...prev, session_date: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Start</Label>
                <Input type="time" value={bookingForm.start_time} onChange={(e) => setBookingForm(prev => ({ ...prev, start_time: e.target.value, price: String(calculatePrice(prev.type, e.target.value, prev.end_time)) }))} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">End</Label>
                <Input type="time" value={bookingForm.end_time} onChange={(e) => setBookingForm(prev => ({ ...prev, end_time: e.target.value, price: String(calculatePrice(prev.type, prev.start_time, e.target.value)) }))} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs uppercase">Session Type</Label>
              <Select value={bookingForm.type} onValueChange={(v) => setBookingForm(prev => ({ ...prev, type: v, price: String(calculatePrice(v, prev.start_time, prev.end_time)) }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#27272a] border-white/10">
                  <SelectItem value="F2F" className="text-white"><MapPin className="w-4 h-4 inline mr-2" />Face-to-Face</SelectItem>
                  <SelectItem value="Online" className="text-white"><Video className="w-4 h-4 inline mr-2" />Online</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status & Payment */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Status</Label>
                <Select value={bookingForm.status} onValueChange={(v) => setBookingForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10">
                    <SelectItem value="Scheduled" className="text-white">Scheduled</SelectItem>
                    <SelectItem value="Completed" className="text-white">Completed</SelectItem>
                    <SelectItem value="Late-Cancel" className="text-white">Late Cancel</SelectItem>
                    <SelectItem value="No-Charge" className="text-white">No Charge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Payment</Label>
                <Select value={bookingForm.payment_status} onValueChange={(v) => setBookingForm(prev => ({ ...prev, payment_status: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10">
                    <SelectItem value="Pending" className="text-white">Pending</SelectItem>
                    <SelectItem value="Paid" className="text-white">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Method */}
            {bookingForm.payment_status === "Paid" && (
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Payment Method</Label>
                <Select value={bookingForm.payment_method} onValueChange={(v) => setBookingForm(prev => ({ ...prev, payment_method: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10">
                    <SelectItem value="Cash" className="text-white">Cash</SelectItem>
                    <SelectItem value="Online" className="text-white">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Price Input — v7.4 fix: editable, defaults from calculatePrice */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs uppercase">Session Price ({"\u00A3"})</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37] font-bold">{"\u00A3"}</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="session-price-input"
                  value={bookingForm.price}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                      setBookingForm(prev => ({ ...prev, price: v }));
                    }
                  }}
                  className="pl-8 bg-[#D4AF37]/10 border-[#D4AF37]/30 text-white font-bold text-lg"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs uppercase">Sifu Notes</Label>
              <Textarea value={bookingForm.sifu_notes} onChange={(e) => setBookingForm(prev => ({ ...prev, sifu_notes: e.target.value }))} placeholder="Training notes, focus areas..." className="bg-white/5 border-white/10 text-white h-20" />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            {editingSession && (
              <Button variant="destructive" onClick={handleDeleteSession}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowBookingModal(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleSaveSession} className="bg-[#D4AF37] text-black hover:bg-[#B8972E]">
              {editingSession ? "Update" : "Book"} Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Link Modal */}
      <Dialog open={showVideoLinkModal} onOpenChange={setShowVideoLinkModal}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Video Lesson Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#A1A1AA]">Enter the video call link for {selectedStudentForVideo?.name}</p>
            <Input type="url" value={videoLink} onChange={(e) => setVideoLink(e.target.value)} placeholder="https://zoom.us/j/... or https://meet.google.com/..." className="bg-white/5 border-white/10 text-white" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVideoLinkModal(false)} className="border-white/20 text-white">Cancel</Button>
            <Button onClick={saveVideoLink} className="bg-blue-600 hover:bg-blue-700">Save & Launch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
